/**
 * Presentation helpers. Kept dependency-free and pure so every component
 * formats numbers, colours and dates identically.
 */

/** Score thresholds that drive the gauge colour. */
export const SCORE_THRESHOLDS = Object.freeze({ strong: 75, neutral: 50 });

const INR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});

const COMPACT = new Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 2 });

/**
 * Format a price in Indian rupees.
 * @param {number | null | undefined} value
 */
export function formatPrice(value) {
  return Number.isFinite(value) ? INR.format(value) : '—';
}

/**
 * Format an already-percentage value (``1.23`` -> ``+1.23%``).
 * @param {number | null | undefined} value
 * @param {{digits?: number}} [options]
 */
export function formatPercent(value, { digits = 2 } = {}) {
  if (!Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(digits)}%`;
}

/**
 * Format a confidence fraction (``0.55`` -> ``55%``).
 * @param {number | null | undefined} value
 */
export function formatConfidence(value) {
  return Number.isFinite(value) ? `${Math.round(value * 100)}%` : '—';
}

/**
 * Compact volume/market-cap style numbers.
 * @param {number | null | undefined} value
 */
export function formatCompact(value) {
  return Number.isFinite(value) ? COMPACT.format(value) : '—';
}

/**
 * Short, locale-stable date string for API timestamps.
 * @param {string | null | undefined} value
 */
export function formatDate(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Colour tone for a Bull Score: green > 75, yellow 50-75, red < 50.
 * @param {number | null | undefined} score
 * @returns {{key: 'bull'|'caution'|'bear', text: string, border: string, stroke: string, chip: string, label: string}}
 */
export function scoreTone(score) {
  const value = Number.isFinite(score) ? score : 0;
  if (value > SCORE_THRESHOLDS.strong) {
    return {
      key: 'bull',
      text: 'text-bull',
      border: 'border-bull/40',
      stroke: '#22c55e',
      chip: 'bg-bull/10 text-bull border-bull/40',
      label: 'Bullish',
    };
  }
  if (value >= SCORE_THRESHOLDS.neutral) {
    return {
      key: 'caution',
      text: 'text-caution',
      border: 'border-caution/40',
      stroke: '#facc15',
      chip: 'bg-caution/10 text-caution border-caution/40',
      label: 'Neutral',
    };
  }
  return {
    key: 'bear',
    text: 'text-bear',
    border: 'border-bear/40',
    stroke: '#ef4444',
    chip: 'bg-bear/10 text-bear border-bear/40',
    label: 'Bearish',
  };
}

/**
 * Tone for a signed percentage change.
 * @param {number | null | undefined} value
 */
export function changeTone(value) {
  if (!Number.isFinite(value) || value === 0) return 'text-slate-400';
  return value > 0 ? 'text-bull' : 'text-bear';
}

/**
 * Human label for a signal value coming from the database.
 * @param {string | null | undefined} signal
 */
export function signalLabel(signal) {
  return signal === 'Strong' ? 'Strong signal' : 'Weak signal';
}
